import { SortComparerResponse } from '@/components/FileManager/types/FileManagerTypes'

export default function useSortComparer(): SortComparerResponse {
  const extractItemData = (reference: any, sortOrder: any, comparer: any, column: any) => {
    const firstItemData = sortOrder && typeof sortOrder === 'object' ? sortOrder : reference
    const secondItemData = column && typeof column === 'object' ? column : comparer
    return { firstItemData, secondItemData }
  }

  const getItemTypes = (firstItemData: any, secondItemData: any) => {
    const isFirstItemFile = firstItemData?.isFile === true
    const isSecondItemFile = secondItemData?.isFile === true
    return { isFirstItemFile, isSecondItemFile }
  }

  const getItemNames = (reference: any, firstItemData: any, comparer: any, secondItemData: any) => {
    const firstItemName = typeof reference === 'string' ? reference : (reference?.name || firstItemData?.name || '')
    const secondItemName = typeof comparer === 'string' ? comparer : (comparer?.name || secondItemData?.name || '')
    return { firstItemName, secondItemName }
  }

  const getSortOrder = (sortOrder: any): string => {
    return typeof sortOrder === 'string' ? sortOrder : 'Ascending'
  }

  const compareFolderAndFile = (isFirstItemFile: boolean, isSecondItemFile: boolean): number | null => {
    if (!isFirstItemFile && isSecondItemFile) {
      return -1
    }
    if (isFirstItemFile && !isSecondItemFile) {
      return 1
    }
    return null
  }

  const splitIntoParts = (text: string): (string | number)[] => {
    const parts: (string | number)[] = []
    text.replace(/(\d+)|(\D+)/g, (_: string, numericPart: string, textPart: string) => {
      parts.push(numericPart || textPart)
      return ''
    })
    return parts
  }

  const compareParts = (firstPart: string | number, secondPart: string | number): number => {
    const firstNumber = Number.parseInt(firstPart as string, 10)
    const secondNumber = Number.parseInt(secondPart as string, 10)

    if (!Number.isNaN(firstNumber) && !Number.isNaN(secondNumber)) {
      return firstNumber - secondNumber
    }

    return String(firstPart).localeCompare(String(secondPart), 'es', {
      numeric: true,
      sensitivity: 'base'
    })
  }

  const naturalCompare = (first: string, second: string): number => {
    const firstParts = splitIntoParts(first)
    const secondParts = splitIntoParts(second)

    while (firstParts.length && secondParts.length) {
      const firstPart = firstParts.shift()!
      const secondPart = secondParts.shift()!

      const comparison = compareParts(firstPart, secondPart)
      if (comparison !== 0) {
        return comparison
      }
    }

    return firstParts.length - secondParts.length
  }

  const customSortComparer = (reference: any, comparer: any, sortOrder?: string, column?: any): number => {
    const { firstItemData, secondItemData } = extractItemData(reference, sortOrder, comparer, column)
    const { isFirstItemFile, isSecondItemFile } = getItemTypes(firstItemData, secondItemData)
    const { firstItemName, secondItemName } = getItemNames(reference, firstItemData, comparer, secondItemData)
    const order = getSortOrder(sortOrder)

    const folderFileComparison = compareFolderAndFile(isFirstItemFile, isSecondItemFile)
    if (folderFileComparison !== null) {
      return folderFileComparison
    }

    const comparisonResult = naturalCompare(firstItemName, secondItemName)

    return order === 'Descending' ? -comparisonResult : comparisonResult
  }

  const setSortComparer = (config: any, sort: any = customSortComparer): any => {
    const configuration: any = config
    configuration?.columns?.forEach((column: any) => {
      if (column?.hasOwnProperty('sortComparer')) {
        column.sortComparer = sort
      }
    })
    return configuration
  }

  return {
    customSortComparer,
    setSortComparer
  }
}
